import styled from '@emotion/styled';
import { FC, KeyboardEvent, ReactNode, useCallback, useState } from 'react';

import Rotate from 'app/components/animations/Rotate';
import VerticalExpander from 'app/components/animations/VerticalExpander';
import Flex from 'app/components/v2/components/Flex';
import Icon from 'app/components/v2/components/Icon';
import MenuItem from 'app/components/v2/components/Menu/MenuItem';
import Legal from 'app/components/v2/components/Text/Legal';
import Paragraph from 'app/components/v2/components/Text/Paragraph';
import { EvtKeys } from 'app/types';
import { getReactNodeText } from 'app/utils/lib/string';

// Local Typings
type SubItemId = null | number | string;

export interface SubItem {
  id: SubItemId;
  label: string;
  onClick: () => void;
  secondaryText?: null | string;
}

export interface AccountMenuItemProps {
  label: ReactNode;
  menuIcon?: ReactNode;
  onClick?: () => void;
  subItemSelectedId?: SubItemId;
  subItems?: SubItem[];
}

// Local Variables
const List = styled.ul({
  maxHeight: 300,
  overflow: 'auto',
});

const getAriaLabel = (label: ReactNode,
  subItems: SubItem[] | undefined) => {
  if (typeof label === 'string') {
    return subItems ? `${label} sub-menu` : `navigate to ${label}`;
  }

  return getReactNodeText(label);
};

// Component Definition
const AccountMenuItem: FC<AccountMenuItemProps> = ({
  label,
  menuIcon,
  onClick,
  subItemSelectedId,
  subItems,
}) => {
  const [isSubItemsOpen, setIsSubItemsOpen] = useState(false);

  const handleClick = useCallback(() => {
    if (subItems) {
      setIsSubItemsOpen(!isSubItemsOpen);
    } else if (onClick) {
      onClick();
    }
  },
  [isSubItemsOpen, onClick, subItems]);

  const handleKeyDown = useCallback(({ key }: KeyboardEvent<HTMLLIElement>) => {
    if (key as EvtKeys === EvtKeys.Enter || key as EvtKeys === EvtKeys.Space) {
      handleClick();
    }
  },
  [handleClick]);

  const handleMenuItemKeyDown = useCallback((subItem: SubItem) => ({ key }: KeyboardEvent<HTMLLIElement>) => {
    if (key as EvtKeys === EvtKeys.Enter || key as EvtKeys === EvtKeys.Space) {
      subItem.onClick();
    }
  },
  []);

  return (
    <MenuItem
      aria-expanded={subItems ? isSubItemsOpen : undefined}
      aria-label={getAriaLabel(label,
        subItems)}
      isSelected={false}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      smallPadding
      tabIndex={0}
      withBorderBottom
    >
      <Flex justifyContent="space-between">
        <Paragraph
          alignItems="center"
          as="span"
          display="flex"
          gap={1}
        >
          {label}
        </Paragraph>
        {menuIcon ?? (
          <Rotate rotationDegrees={isSubItemsOpen ? 90 : 0}>
            <Icon name="caret-right" />
          </Rotate>
        )}
      </Flex>
      {subItems && (
        <VerticalExpander
          isExpanded={isSubItemsOpen}
          transitionSpeedInMs={500}
        >
          <List>
            {subItems.map(subItem => (
              <MenuItem
                isSelected={false}
                key={subItem.label}
                onClick={subItem.onClick}
                onKeyDown={handleMenuItemKeyDown(subItem)}
                smallPadding
                tabIndex={0}
              >
                <Legal
                  fontWeight={subItem.id === subItemSelectedId ? 'semiBold' : 'normal'}
                  marginLeft={4}
                >
                  {subItem.label}
                </Legal>

                {subItem.secondaryText && (
                  <Legal
                    fontWeight="bold"
                    marginLeft={4}
                  >
                    {subItem.secondaryText}
                  </Legal>
                )}
              </MenuItem>
            ))}
          </List>
        </VerticalExpander>
      )}
    </MenuItem>
  );
};

export default AccountMenuItem;
